import { alpha } from "@mui/material/styles";
import type { PaletteOptions } from "@mui/material/styles/createPalette";

import type { ColorPreset, Contrast } from "..";
import { neutral } from "../colors";

interface Config {
  colorPreset?: ColorPreset;
  contrast?: Contrast;
}

export const createPalette = (config: Config): PaletteOptions => {
  const { colorPreset, contrast } = config;

  // Grey-based color palette for dark theme (black, white, grey only)
  const greyPrimary = {
    lightest: "#F5F5F5",
    light: "#E0E0E0",
    main: "#9E9E9E", // Medium grey for primary actions
    dark: "#616161",
    darkest: "#424242",
    contrastText: "#FFFFFF",
    alpha4: alpha("#9E9E9E", 0.04),
    alpha8: alpha("#9E9E9E", 0.08),
    alpha12: alpha("#9E9E9E", 0.12),
    alpha30: alpha("#9E9E9E", 0.3),
    alpha50: alpha("#9E9E9E", 0.5),
  };

  const greyInfo = {
    lightest: "#FAFAFA",
    light: "#E5E5E5",
    main: "#BDBDBD", // Light grey for info
    dark: "#757575",
    darkest: "#616161",
    contrastText: "#000000",
    alpha4: alpha("#BDBDBD", 0.04),
    alpha8: alpha("#BDBDBD", 0.08),
    alpha12: alpha("#BDBDBD", 0.12),
    alpha30: alpha("#BDBDBD", 0.3),
    alpha50: alpha("#BDBDBD", 0.5),
  };

  const greySuccess = {
    lightest: "#F5F5F5",
    light: "#E0E0E0",
    main: "#9E9E9E", // Medium grey for success
    dark: "#616161",
    darkest: "#424242",
    contrastText: "#FFFFFF",
    alpha4: alpha("#9E9E9E", 0.04),
    alpha8: alpha("#9E9E9E", 0.08),
    alpha12: alpha("#9E9E9E", 0.12),
    alpha30: alpha("#9E9E9E", 0.3),
    alpha50: alpha("#9E9E9E", 0.5),
  };

  const greyWarning = {
    lightest: "#FAFAFA",
    light: "#E5E5E5",
    main: "#BDBDBD", // Light grey for warning
    dark: "#757575",
    darkest: "#616161",
    contrastText: "#000000",
    alpha4: alpha("#BDBDBD", 0.04),
    alpha8: alpha("#BDBDBD", 0.08),
    alpha12: alpha("#BDBDBD", 0.12),
    alpha30: alpha("#BDBDBD", 0.3),
    alpha50: alpha("#BDBDBD", 0.5),
  };

  const greyError = {
    lightest: "#F5F5F5",
    light: "#E0E0E0",
    main: "#757575", // Darker grey for error
    dark: "#616161",
    darkest: "#424242",
    contrastText: "#FFFFFF",
    alpha4: alpha("#757575", 0.04),
    alpha8: alpha("#757575", 0.08),
    alpha12: alpha("#757575", 0.12),
    alpha30: alpha("#757575", 0.3),
    alpha50: alpha("#757575", 0.5),
  };

  return {
    action: {
      active: neutral[500],
      disabled: alpha(neutral[100], 0.38),
      disabledBackground: alpha(neutral[100], 0.12),
      focus: alpha(neutral[100], 0.16),
      hover: alpha(neutral[100], 0.04),
      selected: alpha(neutral[100], 0.12),
    },
    background: {
      default: contrast === "high" ? "#000000" : "#1B1B1B", // Pure black or very dark grey
      paper: "#1B1B1B", // Dark grey/black for paper
    },
    divider: "#424242", // Medium grey divider
    error: greyError,
    info: greyInfo,
    mode: "dark",
    neutral,
    primary: greyPrimary,
    success: greySuccess,
    text: {
      primary: "#FFFFFF", // Pure white
      secondary: "#BDBDBD", // Light grey
      disabled: "rgba(255, 255, 255, 0.38)",
    },
    warning: greyWarning,
  };
};
